import React, { FC, useCallback, ChangeEvent } from "react"
import {
    ExclusiveChoiceDropdown,
    Checkbox,
    SingleLineTextInput
} from "dash-styled"

import {
    APIAppMeasurerConfiguration,
    MeasurementScope,
    measurementScopes
} from "../../../../../../../../../../../../Models/API/APIAppMeasurerConfiguration"

interface MeasurementProps {
    value: APIAppMeasurerConfiguration
    onChange: (value: { measurement: APIAppMeasurerConfiguration }) => void
}

function measurementScopeToString(scope: MeasurementScope): string {
    switch (scope) {
        case "selected":
            return "Active plan only"
        case "visible":
            return "All visible plans"
        case "calibration":
            return "Calibration jobs"
        case "off":
            return "Off"
    }
}

export const Measurement: FC<MeasurementProps> = ({ value, onChange }) => {
    const handleChange = useCallback(
        (value: APIAppMeasurerConfiguration) => {
            onChange({
                measurement: value
            })
        },
        [onChange]
    )
    const handleChangeScope = useCallback(
        (scope: MeasurementScope) => handleChange({ ...value, scope }),
        [handleChange, value]
    )
    const handleChangeFreq = useCallback(
        (e: ChangeEvent<HTMLInputElement>) =>
            handleChange({
                ...value,
                completed_data_points_freq: Number.parseInt(e.target.value, 10)
            }),
        [handleChange, value]
    )
    const handleChangeReviewed = useCallback(
        (e: ChangeEvent<HTMLInputElement>) =>
            handleChange({
                ...value,
                reviewed_data_points_per: Number.parseFloat(e.target.value)
            }),
        [handleChange, value]
    )
    const handleChangeGT = useCallback(
        (e: ChangeEvent<HTMLInputElement>) =>
            handleChange({
                ...value,
                newly_ground_truth_per: Number.parseFloat(e.target.value)
            }),
        [handleChange, value]
    )
    const handleToggleFreq = useCallback(
        () =>
            handleChange({
                ...value,
                completed_data_points_freq:
                    (value.completed_data_points_freq ?? 0) > 0 ? 0 : 1
            }),
        [handleChange, value]
    )
    const handleToggleReview = useCallback(
        () =>
            handleChange({
                ...value,
                reviewed_data_points_per:
                    (value.reviewed_data_points_per ?? 0) > 0 ? 0 : 1
            }),
        [handleChange, value]
    )
    const handleToggleGT = useCallback(
        () =>
            handleChange({
                ...value,
                newly_ground_truth_per:
                    (value.newly_ground_truth_per ?? 0) > 0 ? 0 : 1
            }),
        [handleChange, value]
    )

    return (
        <>
            <div className="flex items-center">
                <span className="mr-2">For</span>
                <ExclusiveChoiceDropdown<MeasurementScope>
                    size="sm"
                    testId="measurement-scope"
                    className="w-48 mr-2"
                    value={value.scope}
                    choices={measurementScopes}
                    renderChoice={measurementScopeToString}
                    onClick={handleChangeScope}
                />
                {value.scope !== "off" ? " After:" : ""}
            </div>
            {value.scope === "off" ? null : (
                <ul className="pt-2">
                    <li className="flex items-center pb-2">
                        <Checkbox
                            testId="measurement-freq-checkbox"
                            variant="modern"
                            isChecked={value.completed_data_points_freq !== 0}
                            onChange={handleToggleFreq}
                        />
                        Every
                        <SingleLineTextInput
                            testId="measurement-freq-value"
                            className="w-20 mx-1"
                            size="sm"
                            type="number"
                            transparentBg={true}
                            value={
                                Number.isNaN(value.completed_data_points_freq)
                                    ? ""
                                    : String(value.completed_data_points_freq)
                            }
                            onChange={handleChangeFreq}
                        />
                        completed data points
                    </li>
                    <li className="flex items-center pb-2">
                        <Checkbox
                            testId="measurement-review-sample-checkbox"
                            variant="modern"
                            isChecked={value.reviewed_data_points_per !== 0}
                            onChange={handleToggleReview}
                        />
                        <SingleLineTextInput
                            testId="measurement-review-sample-value"
                            className="w-20 mr-1"
                            size="sm"
                            type="number"
                            transparentBg={true}
                            value={
                                Number.isNaN(value.reviewed_data_points_per)
                                    ? ""
                                    : String(value.reviewed_data_points_per)
                            }
                            onChange={handleChangeReviewed}
                        />
                        % of reviewed data points
                    </li>
                    <li className="flex items-center">
                        <Checkbox
                            testId="measurement-gt-sample-checkbox"
                            variant="modern"
                            isChecked={value.newly_ground_truth_per !== 0}
                            onChange={handleToggleGT}
                        />
                        <SingleLineTextInput
                            testId="measurement-gt-sample-value"
                            className="w-20 mr-1"
                            size="sm"
                            type="number"
                            transparentBg={true}
                            value={
                                Number.isNaN(value.newly_ground_truth_per)
                                    ? ""
                                    : String(value.newly_ground_truth_per)
                            }
                            onChange={handleChangeGT}
                        />
                        % of newly uploaded ground truth
                    </li>
                </ul>
            )}
        </>
    )
}
